import type { Transform } from "#root/utility/transforms";
import { Invalid, Neutral, Valid, type Validator } from "#root/utility/validators";
import { type InputHTMLAttributes, useCallback, useMemo, useState } from "react";
import { useFieldSet } from "./fieldset";

type Validity = typeof Valid | typeof Invalid | typeof Neutral;

interface ValidationResult {
  validity: Validity;
  hint: string;
}

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  inboundTransform?: Transform<unknown, string | undefined>;
  outboundTransform?: Transform<string | undefined, unknown>;
  validator?: Validator;
  required?: boolean;
  dirty?: boolean;
  defaultValue?: string;
};

export function InputField({
                        inboundTransform = (value: unknown) => (value ? String(value) : undefined),
                        outboundTransform = (value: string | undefined) => value,
                        validator,
                        required,
                        dirty: startDirty,
                        defaultValue = "",
                        prefix,
                        className,
                        autoComplete = "autocomplete",
                        ...inputProps
                      }: InputProps) {
  const fieldSet = useFieldSet();

  const [value, setValue] = useState<string | undefined>(fieldSet?.field?.value == null ? defaultValue : inboundTransform(fieldSet?.field?.value));
  const [dirty, setDirty] = useState<boolean | undefined>(startDirty);

  const { validity, hint } = useMemo<ValidationResult>(() => {
    if (value == null || value.length === 0) {
      if (required) {
        return { validity: Invalid, hint: "Required" };
      }

      return { validity: Neutral, hint: "Optional" };
    }

    if (validator) {
      return validator(value) || { validity: Valid, hint: "Valid" };
    }

    return { validity: Valid, hint: "Valid" };
  }, [value, required, validator]);

  useMemo(() => {
    fieldSet?.mutate((object: Record<string, unknown>) => ({
      ...object,
      dirty,
      validity,
      hint,
      value: required || value ? outboundTransform(value) : null,
    }));
  }, [fieldSet, dirty, validity, hint, value, outboundTransform]);

  let validityClassName = "neutral";
  switch (validity) {
    case Valid: {
      validityClassName = "valid";
      break;
    }

    case Invalid: {
      validityClassName = "invalid";
      break;
    }
  }

  const handleChange = useCallback((evnt: { currentTarget: { value: string } }) => {
    setValue(evnt.currentTarget.value);
    setDirty(true);
  }, []);

  const handleFocus = useCallback(() => {
    fieldSet?.mutate((object: Record<string, unknown>) => ({ ...object, focused: true }));
  }, [fieldSet]);

  const handleBlur = useCallback(() => {
    fieldSet?.mutate((object: Record<string, unknown>) => ({ ...object, focused: false }));
  }, [fieldSet]);

  const borderColor = validity === Invalid ? "border-red-400" : validity === Valid ? "border-green-400" : "border-[#ddd]";

  return (
    <div className="flex" style={inputProps.type === "hidden" ? { display: "none" } : undefined}>
      {prefix ?
        <span className="inline-flex items-center px-3 border border-r-0 border-[#ddd] rounded-l bg-[#f5f5f5] text-[#666] text-[1.3rem]">{prefix}</span> : null}
      <input
        {...inputProps}
        className={`w-full border ${borderColor} ${prefix ? "rounded-r" : "rounded"} px-3 py-2 text-base text-[#333] outline-none focus:border-[#39f] focus:ring-2 focus:ring-[#39f]/20 transition-colors bg-white ${className ?? ""}`}
        value={value}
        autoComplete={typeof autoComplete === "boolean" ? (autoComplete ? "on" : "off") : autoComplete}
        onChange={handleChange}
        onInput={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </div>
  );
}
